import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listVendorDirectoryImportJobs } from "../../api/clientVendorDirectoryImportApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.importsPage";

function importStatusBadgeClass(status) {
  if (status === "completed") return "cvd-badge--published";
  if (status === "failed" || status === "cancelled") return "cvd-badge--suspended";
  if (status === "processing") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

export default function ClientVendorDirectoryImportListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t } = useLocale();
  const paths = useClientPortalPaths();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") || "";
  const page = Number(searchParams.get("page") || 1);
  const basePath = `${serviciosBase}/importaciones`;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryImportJobs(clientViewId, {
        page,
        pageSize: 25,
        status: status || undefined,
      });
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">
            {t(`${COPY_PREFIX}.intro`, { viewName: activeView?.name || t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.thisView") })}
          </p>
        </div>
        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_CREATE}>
          <div className="cvd-admin__actions">
            <Link className="btn btn-primary" to={`${basePath}/nuevo`}>
              {t(`${COPY_PREFIX}.newImport`)}
            </Link>
          </div>
        </VendorDirectoryPermissionGate>
      </div>

      <div className="cvd-filters">
        <div className="cvd-field">
          <label htmlFor="import-status">Estado</label>
          <select
            id="import-status"
            value={status}
            onChange={(e) => updateParam("status", e.target.value)}
          >
            <option value="">Todos</option>
            <option value="draft">Borrador</option>
            <option value="uploaded">Archivo cargado</option>
            <option value="mapping">Mapeo</option>
            <option value="validating">Validando</option>
            <option value="ready">Listo</option>
            <option value="processing">Procesando</option>
            <option value="completed">Completado</option>
            <option value="completed_with_errors">Completado con errores</option>
            <option value="failed">Fallido</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
      </div>

      {loading && <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />}
      {!loading && errorMessage && <ClientPortalError message={errorMessage} onRetry={load} />}
      {!loading && !errorMessage && (
        <div className="cvd-table-wrap">
          <table className="cvd-table">
            <thead>
              <tr>
                <th>Archivo</th>
                <th>Estado</th>
                <th>Tipo</th>
                <th>Creado</th>
                <th>Filas</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.jobs || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No hay importaciones.
                  </td>
                </tr>
              ) : (
                (data?.jobs || []).map((job) => (
                  <tr key={job.id}>
                    <td>{job.originalFilename || "—"}</td>
                    <td>
                      <span className={`cvd-badge ${importStatusBadgeClass(job.status)}`}>
                        {formatStatusLabel(job.status)}
                      </span>
                    </td>
                    <td>{job.importType || "csv"}</td>
                    <td>{formatClientDate(job.createdAt)}</td>
                    <td>{job.recordsReceived ?? "—"}</td>
                    <td>
                      <Link to={`${basePath}/${job.id}`}>Abrir</Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
